import type { IComponent } from "@Components/Component";
import { Component } from "@Components/Component";
import { Modal } from "@Components/Modal";
import { Orientation, calculateViewport } from "@Components/Modules/ViewportResize";
import * as stylex from "@stylexjs/stylex";

import { genericStyles } from "../../styles/tokens.stylex";

import * as css from "./DiscountCoupon.module.css";

export enum DiscountCouponType {
    Code,
    Link,
    Image,
    MobileOnly,
}

export interface IDiscountCouponBase extends IComponent {
    title: string;
    description: string;
}

interface DiscountCouponState {
    showModal: boolean;
    orientation: Orientation;
}

abstract class DiscountCouponBase<TProps extends IDiscountCouponBase> extends Component<
    TProps,
    DiscountCouponState
> {
    constructor(props: TProps) {
        super(props);

        this.state = {
            showModal: false,
            orientation: Orientation.Landscape,
        };
    }

    public componentDidMount(): void {
        window.addEventListener("resize", this.onResize);
        this.onResize();
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this.onResize);
    }

    private onResize = () => {
        const orientation =
            window.screen.width > window.screen.height ? Orientation.Landscape : Orientation.Portrait;

        if (orientation !== this.state.orientation) {
            const { isMobileOrTablet } = calculateViewport(window.innerWidth);

            this.setState({
                showModal: isMobileOrTablet,
                orientation,
            });
        }
    };

    protected abstract couponType: DiscountCouponType;

    protected abstract renderCoupon: () => JSX.Element;

    protected renderTitle() {
        return <div className={css.title}>{this.props.title}</div>;
    }

    protected renderDescription() {
        return <div className={css.description}>{this.props.description}</div>;
    }

    protected renderContent() {
        return (
            <div className={`${css.couponContent} ${stylex.props(styles.border).className}`}>
                {this.renderCoupon()}
            </div>
        );
    }

    private renderCouponType() {
        switch (this.couponType) {
            case DiscountCouponType.Link:
                return (
                    <>
                        {this.renderTitle()}
                        {this.renderDescription()}
                        {this.renderContent()}
                    </>
                );
            default:
                return (
                    <>
                        {this.renderTitle()}
                        {this.renderContent()}
                        {this.renderDescription()}
                    </>
                );
        }
    }

    public render() {
        let modal = null;
        if (this.state.showModal === true && this.state.orientation === Orientation.Landscape) {
            const modalClose = () => {
                this.setState({ showModal: false });
            };

            modal = (
                <Modal
                    clientId="coupon-modal"
                    className={css.discountCouponModal}
                    isOpen={true}
                    onClose={modalClose}
                >
                    {this.renderContent()}
                </Modal>
            );
        }

        return (
            <div
                {...this.rootDefaultAttributes(
                    css.discountCouponComponent,
                    stylex.props(styles.border).className,
                )}
            >
                {this.renderCouponType()}
                {modal}
            </div>
        );
    }
}

const styles = stylex.create({
    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },
});

export { DiscountCouponBase };
